/**
 * FieldFileInput — wspólny komponent pola typu „plik” (ADR-0114).
 *
 * Wartością pola jest id pliku z `/api/field-files`. Wybór pliku wysyła go od razu jako plik
 * ROBOCZY; do rekordu przypina go dopiero zapis rekordu (Zapisz) — dlatego „Anuluj” na
 * formularzu nic nie psuje, a porzucony plik roboczy sprząta serwer.
 *
 *  - pusto: przycisk „Wybierz plik”,
 *  - wysyłanie: wskaźnik postępu,
 *  - jest plik: nazwa (klik = pobranie) + „Zmień” + „Usuń”.
 *
 * `FieldFileLink` — sama nazwa z pobraniem, do widoku tylko do odczytu (komórka tabeli, podgląd).
 *
 * Nazwę pliku zna rodzic (mapa `fileMeta` z odczytu albo wynik wysłania) — komponent nie pyta
 * serwera o metadane.
 */

import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Paperclip, RefreshCw, X } from 'lucide-react';

import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import {
  downloadFieldFile,
  FIELD_FILE_MAX_BYTES,
  type FieldFileMeta,
  type FieldFileTarget,
  fieldFileUploadErrorCode,
  uploadFieldFile,
} from '@/lib/field-files/api';
import { cn } from '@/lib/utils';

/** Kody błędów wysyłania z czytelnym komunikatem (reszta → komunikat ogólny). */
const KNOWN_UPLOAD_ERRORS = new Set([
  'FILE_TOO_LARGE',
  'MIME_TYPE_NOT_ALLOWED',
  'CONTENT_TYPE_MISMATCH',
  'CONTENT_TYPE_UNVERIFIABLE',
  'FORBIDDEN',
]);

/** Etykieta pliku: nazwa z metadanych albo neutralne „Plik”, gdy nazwa nieznana. */
function useFileLabel(meta: FieldFileMeta | null | undefined): string {
  const { t } = useTranslation();
  return meta?.fileName ?? t('common.fieldFile.unknownName');
}

interface FieldFileLinkProps {
  fileId: string;
  meta?: FieldFileMeta | null;
  className?: string;
  'data-testid'?: string;
}

/** Nazwa pliku jako link pobrania — widok tylko do odczytu. */
export function FieldFileLink({ fileId, meta, className, ...rest }: FieldFileLinkProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const label = useFileLabel(meta);

  async function handleDownload() {
    try {
      await downloadFieldFile(fileId, label);
    } catch {
      toast.show({ variant: 'error', title: t('common.fieldFile.downloadError') });
    }
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn('h-7 max-w-full px-1.5 text-accent-600 hover:text-accent-700', className)}
      onClick={(e) => {
        // Komórka tabeli jest klikalna (edycja w miejscu) — pobranie nie może jej uruchamiać.
        e.stopPropagation();
        void handleDownload();
      }}
      title={t('common.fieldFile.downloadNamed', { name: label })}
      data-testid={rest['data-testid']}
    >
      <Paperclip className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="truncate">{label}</span>
    </Button>
  );
}

interface FieldFileInputProps {
  /** Id pliku (wartość pola) albo `null`. */
  value: string | null;
  /** Metadane pliku z `value` — z odczytu rekordu albo z wysłania. */
  meta?: FieldFileMeta | null;
  /** Gdzie żyje pole (kolumna tabeli + rekord-rodzic). */
  target: FieldFileTarget;
  /** Nowa wartość: id wysłanego pliku z jego metadanymi albo `null` (usunięcie). */
  onChange: (fileId: string | null, meta: FieldFileMeta | null) => void;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function FieldFileInput({
  value,
  meta,
  target,
  onChange,
  disabled = false,
  className,
  ...rest
}: FieldFileInputProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const testId = rest['data-testid'] ?? 'field-file';
  // Pole zniknęło w trakcie wysyłki (✕ w wierszu dodawania, „Anuluj” formularza) — wynik
  // wysyłki nie może już zmieniać wartości (plik zostaje roboczy i sprząta go serwer).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset, żeby ponowny wybór tego samego pliku też wywołał zmianę.
    e.target.value = '';
    if (!file) return;
    if (file.size > FIELD_FILE_MAX_BYTES) {
      toast.show({ variant: 'error', title: t('common.fieldFile.errors.FILE_TOO_LARGE') });
      return;
    }
    setUploading(true);
    try {
      const uploaded = await uploadFieldFile(target, file);
      if (!mountedRef.current) return;
      onChange(uploaded.id, {
        fileName: uploaded.fileName,
        mimeType: uploaded.mimeType,
        sizeBytes: uploaded.sizeBytes,
      });
    } catch (err) {
      const code = fieldFileUploadErrorCode(err);
      toast.show({
        variant: 'error',
        title:
          code && KNOWN_UPLOAD_ERRORS.has(code)
            ? t(`common.fieldFile.errors.${code}`)
            : t('common.fieldFile.uploadError', { name: file.name }),
      });
    } finally {
      if (mountedRef.current) setUploading(false);
    }
  }

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      className="hidden"
      onChange={(e) => void handleFile(e)}
      disabled={disabled || uploading}
      // Plik wysyła się od razu jako roboczy — sama wartość pola zmienia się przez onChange.
      data-no-dirty
      data-testid={`${testId}-input`}
    />
  );
  const openPicker = () => inputRef.current?.click();

  if (uploading) {
    return (
      <div
        className={cn('flex h-8 items-center gap-2 text-xs text-text-tertiary', className)}
        data-testid={`${testId}-uploading`}
      >
        <Spinner size="sm" label={t('common.fieldFile.uploading')} />
        {t('common.fieldFile.uploading')}
      </div>
    );
  }

  if (!value) {
    return (
      <div className={cn('flex items-center', className)}>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="h-8"
          onClick={openPicker}
          disabled={disabled}
          data-testid={`${testId}-pick`}
        >
          <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
          {t('common.fieldFile.pick')}
        </Button>
        {fileInput}
      </div>
    );
  }

  return (
    <div className={cn('flex min-w-0 items-center gap-0.5', className)}>
      <FieldFileLink
        fileId={value}
        meta={meta}
        className="min-w-0 flex-1 justify-start"
        data-testid={`${testId}-name`}
      />
      {!disabled && (
        <>
          <IconButton
            size="sm"
            onClick={openPicker}
            aria-label={t('common.fieldFile.replace')}
            title={t('common.fieldFile.replace')}
            data-testid={`${testId}-replace`}
          >
            <RefreshCw />
          </IconButton>
          <IconButton
            size="sm"
            onClick={() => onChange(null, null)}
            aria-label={t('common.fieldFile.remove')}
            title={t('common.fieldFile.remove')}
            data-testid={`${testId}-remove`}
          >
            <X />
          </IconButton>
        </>
      )}
      {fileInput}
    </div>
  );
}
